import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  Descriptions,
  Avatar,
  Tabs,
  Table,
  Spin,
  Typography,
  Empty,
  Image,
  Row,
  Col,
  message,
  Modal,
  Form,
  InputNumber,
  Input,
  Tag,
  Space,
} from 'antd';
import { ArrowLeftOutlined, UserOutlined, WalletOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import request from '@/utils/request';
import { buildImageUrl } from '@zero/shared';

const { Title } = Typography;

interface UserDetail {
  id: number;
  avatarUrl?: string;
  nickname?: string;
  openId?: string;
  unionId?: string;
  phone?: string;
  coinBalance?: number;
  lastLogin?: string;
  createdAt?: string;
}

interface ActivityRecord {
  id: number;
  action: string;
  time: string;
  ip?: string;
  detail?: string;
}

interface UserImage {
  id: number;
  accessUrl: string;
  createdAt?: string;
}

const UserDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [user, setUser] = useState<UserDetail | null>(null);
  const [loadingUser, setLoadingUser] = useState(true);

  const [activities, setActivities] = useState<ActivityRecord[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [activityPagination, setActivityPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
  });

  const [images, setImages] = useState<UserImage[]>([]);
  const [loadingImages, setLoadingImages] = useState(false);

  const [topupOpen, setTopupOpen] = useState(false);
  const [topupForm] = Form.useForm<{ coins: number; note?: string }>();
  const [topupLoading, setTopupLoading] = useState(false);

  const fetchUser = useCallback(async () => {
    try {
      setLoadingUser(true);
      const { data } = await request.get(`/admin/users/${id}`);
      setUser(data);
    } catch {
      message.error('Failed to load user details');
    } finally {
      setLoadingUser(false);
    }
  }, [id]);

  const fetchActivities = useCallback(
    async (page = 1, pageSize = 10) => {
      try {
        setLoadingActivities(true);
        const { data } = await request.get(`/admin/users/${id}/activity`, {
          params: { page, pageSize },
        });
        const list = Array.isArray(data) ? data : data.data ?? [];
        const total = data.total ?? list.length;
        setActivities(list);
        setActivityPagination({ page, pageSize, total });
      } catch {
        setActivities([]);
      } finally {
        setLoadingActivities(false);
      }
    },
    [id],
  );

  const fetchImages = useCallback(async () => {
    try {
      setLoadingImages(true);
      const { data } = await request.get(`/admin/users/${id}/images`);
      setImages(Array.isArray(data) ? data : data.data ?? []);
    } catch {
      setImages([]);
    } finally {
      setLoadingImages(false);
    }
  }, [id]);

  useEffect(() => {
    fetchUser();
    fetchActivities();
    fetchImages();
  }, [fetchUser, fetchActivities, fetchImages]);

  const onTopup = async () => {
    if (!user) return;
    try {
      const values = await topupForm.validateFields();
      setTopupLoading(true);
      await request.post(`/admin/consumers/${user.id}/topup`, {
        coins: values.coins,
        note: values.note,
      });
      message.success(values.coins > 0 ? '已充值' : '已扣减');
      setTopupOpen(false);
      topupForm.resetFields();
      fetchUser();
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    } finally {
      setTopupLoading(false);
    }
  };

  const activityColumns: ColumnsType<ActivityRecord> = [
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: 200,
    },
    {
      title: 'Detail',
      dataIndex: 'detail',
      key: 'detail',
      ellipsis: true,
      render: (val: string) => val || '-',
    },
    {
      title: 'Time',
      dataIndex: 'time',
      key: 'time',
      width: 180,
      render: (val: string) =>
        val ? dayjs(val).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      title: 'IP',
      dataIndex: 'ip',
      key: 'ip',
      width: 140,
      render: (val: string) => val || '-',
    },
  ];

  if (loadingUser) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!user) {
    return (
      <div>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/users')}
          style={{ marginBottom: 16 }}
        >
          Back to Users
        </Button>
        <Empty description="User not found" />
      </div>
    );
  }

  return (
    <div>
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/users')}
        style={{ marginBottom: 16 }}
      >
        Back to Users
      </Button>

      <Title level={4} style={{ marginBottom: 24 }}>
        User Detail
      </Title>

      <Card style={{ marginBottom: 24 }}>
        <Descriptions
          bordered
          column={{ xs: 1, sm: 2, lg: 3 }}
          labelStyle={{ fontWeight: 500, width: 120 }}
        >
          <Descriptions.Item label="Avatar">
            <Avatar
              src={buildImageUrl(user.avatarUrl || '', { w: 96, h: 96 })}
              icon={<UserOutlined />}
              size={48}
            />
          </Descriptions.Item>
          <Descriptions.Item label="Nickname">
            {user.nickname || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="ID">{user.id}</Descriptions.Item>
          <Descriptions.Item label="OpenID" span={2}>
            <Typography.Text copyable style={{ wordBreak: 'break-all' }}>
              {user.openId || '-'}
            </Typography.Text>
          </Descriptions.Item>
          <Descriptions.Item label="UnionID">
            <Typography.Text
              copyable={!!user.unionId}
              style={{ wordBreak: 'break-all' }}
            >
              {user.unionId || '-'}
            </Typography.Text>
          </Descriptions.Item>
          <Descriptions.Item label="Phone">
            {user.phone || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="虚拟币余额">
            <Space>
              <Tag color="gold" style={{ margin: 0 }}>
                {(user.coinBalance ?? 0).toLocaleString()}
              </Tag>
              <Button
                size="small"
                type="link"
                icon={<WalletOutlined />}
                onClick={() => setTopupOpen(true)}
              >
                调整
              </Button>
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Last Login">
            {user.lastLogin
              ? dayjs(user.lastLogin).format('YYYY-MM-DD HH:mm:ss')
              : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Created At">
            {user.createdAt
              ? dayjs(user.createdAt).format('YYYY-MM-DD HH:mm:ss')
              : '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Modal
        title={`为 ${user.nickname || user.openId || `用户 ${user.id}`} 调整虚拟币余额`}
        open={topupOpen}
        onOk={onTopup}
        confirmLoading={topupLoading}
        onCancel={() => {
          setTopupOpen(false);
          topupForm.resetFields();
        }}
        destroyOnClose
      >
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 12 }}>
          当前余额：{(user.coinBalance ?? 0).toLocaleString()} 虚拟币 · 正数=充值，负数=扣减
        </Typography.Paragraph>
        <Form form={topupForm} layout="vertical">
          <Form.Item
            label="变动数量（虚拟币）"
            name="coins"
            rules={[
              { required: true, message: '请输入数量' },
              {
                validator: (_, value) =>
                  typeof value === 'number' && value !== 0
                    ? Promise.resolve()
                    : Promise.reject(new Error('数量不能为 0')),
              },
            ]}
          >
            <InputNumber
              step={100}
              min={-1000000}
              max={1000000}
              style={{ width: '100%' }}
              placeholder="例如 1000 充值，-100 扣减"
            />
          </Form.Item>
          <Form.Item label="备注" name="note">
            <Input.TextArea rows={2} placeholder="给账目记录留个原因，便于事后对账" />
          </Form.Item>
        </Form>
      </Modal>

      <Card>
        <Tabs
          defaultActiveKey="activity"
          items={[
            {
              key: 'activity',
              label: 'Activity',
              children: (
                <Table<ActivityRecord>
                  columns={activityColumns}
                  dataSource={activities}
                  rowKey={(record) => record.id ?? `${record.action}-${record.time}`}
                  loading={loadingActivities}
                  pagination={{
                    current: activityPagination.page,
                    pageSize: activityPagination.pageSize,
                    total: activityPagination.total,
                    showSizeChanger: true,
                    onChange: (page, pageSize) =>
                      fetchActivities(page, pageSize),
                  }}
                  scroll={{ x: 660 }}
                  locale={{
                    emptyText: (
                      <Empty description="No activity records" />
                    ),
                  }}
                />
              ),
            },
            {
              key: 'images',
              label: 'Images',
              children: (
                <Spin spinning={loadingImages}>
                  {images.length > 0 ? (
                    <Image.PreviewGroup>
                      <Row gutter={[16, 16]}>
                        {images.map((img) => (
                          <Col xs={12} sm={8} md={6} lg={4} key={img.id}>
                            <Image
                              src={buildImageUrl(img.accessUrl, { w: 300, h: 300 })}
                              preview={{ src: img.accessUrl }}
                              style={{
                                width: '100%',
                                height: 120,
                                objectFit: 'cover',
                                borderRadius: 8,
                              }}
                              fallback="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBkb21pbmFudC1iYXNlbGluZT0ibWlkZGxlIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjYmZiZmJmIiBmb250LXNpemU9IjE0Ij5ObyBJbWFnZTwvdGV4dD48L3N2Zz4="
                            />
                          </Col>
                        ))}
                      </Row>
                    </Image.PreviewGroup>
                  ) : (
                    <Empty description="No images uploaded" />
                  )}
                </Spin>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default UserDetailPage;
